import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Dimensions, Linking, TouchableOpacity} from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import type {ICarouselInstance} from 'react-native-reanimated-carousel';
import {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  SharedValue,
} from 'react-native-reanimated';
import Lottie from 'lottie-react-native';
import FastImage from 'react-native-fast-image';
import {RFValue} from 'react-native-responsive-fontsize';
import {useNavigation} from '@react-navigation/native';
import analytics from '@react-native-firebase/analytics';
import {useGlobal} from '../../../../context/global';
import {useLocalization} from '../../../../context/localization';
import {InfluencerBottomSheet} from '../InfluencerBottomSheet';
import bannerInfluencer from './lotties/BannerInfluencer.json';
import bannerPosteEGanheLottie from './lotties/BannerPosteEGanheV2.json';
import bannerLavagemLottie from './lotties/BannerLavagem3.json';
import bannerEmplacamentoLottie from './lotties/BannerEmplacamentoV3.json';
import bannerGasDeCozinhaLottie from './lotties/BannerGasDeCozinhaV2.json';
import bannerFlamengoLottie from './lotties/BannerFlamengo.json';
import bannerWhatsLottie from './lotties/BannerWhats.json';
import bannerClubeDeVantagens1Lottie from './lotties/BannerClubeDeVantagens1.json';
import bannerClubeDeVantagens2Lottie from './lotties/BannerClubeDeVantagens2.json';
import {CarouselWrapper, Dot, DotBtn, DotFill, DotsRow} from './styles';
import { useRemoteConfig } from '../../../../context/useRemoteConfig';

const SCREEN_W = Dimensions.get('window').width;
const H_MARGIN = 6;
const CARD_W = SCREEN_W - 2 * H_MARGIN;
const CARD_H = Math.round(CARD_W / (42 / 9));

const DEFAULT_DURATION_MS = 5000;

function getLottieDurationMs(
  source: any,
  fallback = DEFAULT_DURATION_MS,
): number {
  const fr = source?.fr;
  const ip = source?.ip ?? 0;
  const op = source?.op;
  if (!fr || !op) {
    return fallback;
  }
  return ((op - ip) / fr) * 1000;
}

type Banner = {
  id: number;
  source: any;
  kind?: 'lottie' | 'image';
  durationMs: number;
  action: () => void;
};

function ImageBannerSlide({source}: {source: object}) {
  return (
    <FastImage
      source={source}
      style={{width: CARD_W, height: CARD_H}}
      resizeMode="cover"
    />
  );
}

function AnimatedDot({
  active,
  progress,
}: {
  active: boolean;
  progress: SharedValue<number>;
}) {
  const fillStyle = useAnimatedStyle(
    () => ({
      width: active ? `${progress.value * 100}%` : '0%',
    }),
    [active],
  );
  return (
    <Dot active={active}>
      <DotFill style={fillStyle} />
    </Dot>
  );
}

function LottieBannerSlide({
  source,
  isActive,
}: {
  source: object;
  isActive: boolean;
}) {
  const animationRef = useRef<Lottie>(null);
  useEffect(() => {
    if (isActive) {
      animationRef.current?.reset();
      animationRef.current?.play();
    } else {
      animationRef.current?.reset();
    }
  }, [isActive]);
  return (
    <Lottie
      ref={animationRef}
      source={source as any}
      loop
      style={{width: CARD_W, height: CARD_H}}
      resizeMode="cover"
    />
  );
}

interface HomeBannerCarouselProps {
  onOpenClubeModal: () => void;
}

export function HomeBannerCarousel({onOpenClubeModal}: HomeBannerCarouselProps) {
  const navigation = useNavigation();
  const {fetchBannerFlags} = useGlobal();
  const {numeroSuporteInfluenciador} = useRemoteConfig(true);
  const {locationIsActive} = useLocalization();
  const [showInfluencerModal, setShowInfluencerModal] = useState(false);
  const [, setInfluencerLink] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<ICarouselInstance>(null);
  const progress = useSharedValue(0);

  function openInfluencer(link: string) {
    setInfluencerLink(link);
    setShowInfluencerModal(true);
  }

  function navigateToStations(tipo: string) {
    if (locationIsActive) {
      navigation.navigate('ListagemPostos', {isUserLocation: true, tipo});
    } else {
      navigation.navigate('Localization', {tipo});
    }
  }

  useEffect(() => {
    fetchBannerFlags();
  }, []);

  const banners: Banner[] = useMemo(
    () => [
      {
        id: 23,
        source: bannerInfluencer,
        durationMs: getLottieDurationMs(bannerInfluencer),
        action: () => {
          analytics().logEvent('home_banner_influencer');
          openInfluencer(
            `https://wa.me/${numeroSuporteInfluenciador}?text=Oi!%20Quero%20saber%20como%20funciona%20o%20Influencer%20Barat%C3%A3o`,
          );
        },
      },
      {
        id: 1,
        source: bannerLavagemLottie,
        durationMs: getLottieDurationMs(bannerLavagemLottie),
        action: () => {
          analytics().logEvent('home_banner_lavagem');
          navigateToStations('LA');
        },
      },
      {
        id: 0,
        source: bannerPosteEGanheLottie,
        durationMs: getLottieDurationMs(bannerPosteEGanheLottie),
        action: () => {
          analytics().logEvent('home_banner_poste_e_ganhe');
          openInfluencer(
            `https://wa.me/${numeroSuporteInfluenciador}?text=Oi!%20Quero%20saber%20como%20funciona%20o%20Influencer%20Barat%C3%A3o`,
          );
        },
      },
      {
        id: 2,
        source: bannerEmplacamentoLottie,
        durationMs: getLottieDurationMs(bannerEmplacamentoLottie),
        action: () => {
          analytics().logEvent('home_banner_emplacamento');
          navigateToStations('E');
        },
      },
      {
        id: 3,
        source: bannerGasDeCozinhaLottie,
        durationMs: getLottieDurationMs(bannerGasDeCozinhaLottie),
        action: () => {
          analytics().logEvent('home_banner_gas_cozinha');
          navigateToStations('G');
        },
      },
      {
        id: 4,
        source: bannerFlamengoLottie,
        durationMs: getLottieDurationMs(bannerFlamengoLottie),
        action: () => {
          analytics().logEvent('home_banner_flamengo');
          navigation.navigate('CardsWallet');
        },
      },
      {
        id: 5,
        source: bannerWhatsLottie,
        durationMs: getLottieDurationMs(bannerWhatsLottie),
        action: () => {
          analytics().logEvent('home_banner_whats');
          Linking.openURL(
            'https://www.whatsapp.com/channel/0029VabnV1M5kg7FSSj0Ip2R',
          );
        },
      },
      {
        id: 6,
        source: bannerClubeDeVantagens1Lottie,
        durationMs: getLottieDurationMs(bannerClubeDeVantagens1Lottie),
        action: () => {
          analytics().logEvent('home_banner_clube_vantagens');
          onOpenClubeModal();
        },
      },
      {
        id: 7,
        source: bannerClubeDeVantagens2Lottie,
        durationMs: getLottieDurationMs(bannerClubeDeVantagens2Lottie),
        action: () => {
          analytics().logEvent('home_banner_clube_vantagens2');
          onOpenClubeModal();
        },
      },
    ],
    [navigation, onOpenClubeModal, locationIsActive, numeroSuporteInfluenciador],
  );

  useEffect(() => {
    const duration = banners[activeIndex]?.durationMs ?? DEFAULT_DURATION_MS;
    progress.value = 0;
    progress.value = withTiming(1, {
      duration,
      easing: Easing.linear,
    });
    const timer = setTimeout(() => {
      carouselRef.current?.next();
    }, duration);
    return () => clearTimeout(timer);
  }, [activeIndex, banners, progress]);

  return (
    <>
      <CarouselWrapper>
        <Carousel
          ref={carouselRef}
          loop
          width={SCREEN_W}
          height={CARD_H}
          mode="parallax"
          modeConfig={{
            parallaxScrollingScale: 0.88,
            parallaxScrollingOffset: 70,
          }}
          data={banners}
          onSnapToItem={setActiveIndex}
          renderItem={({item, index}: {item: Banner; index: number}) => (
            <TouchableOpacity
              activeOpacity={0.85}
              onPress={item.action}
              style={{
                flex: 1,
                marginHorizontal: H_MARGIN,
                borderRadius: RFValue(10),
                overflow: 'hidden',
              }}>
              {item.kind === 'image' ? (
                <ImageBannerSlide source={item.source} />
              ) : (
                <LottieBannerSlide
                  source={item.source}
                  isActive={activeIndex === index}
                />
              )}
            </TouchableOpacity>
          )}
        />
        <DotsRow>
          {banners.map((_, i) => (
            <DotBtn
              key={i}
              onPress={() => carouselRef.current?.scrollTo({index: i, animated: true})}
              activeOpacity={0.8}>
              <AnimatedDot active={activeIndex === i} progress={progress} />
            </DotBtn>
          ))}
        </DotsRow>
      </CarouselWrapper>
      <InfluencerBottomSheet
        isOpen={showInfluencerModal}
        onClose={() => setShowInfluencerModal(false)}
      />
    </>
  );
}
